import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";

import {
  getGetMeQueryKey,
  useUpdatePractice,
} from "@elation/api-client/src/stitch-web";

import {
  PracticeEntity,
  PracticeInfoUpdatePayloadSchema,
  useCanEditPractice,
  useCurrentPractice,
  usePracticeId,
} from "@src/features/practices";
import { PracticeInfoSettingsPageV2 } from "@src/features/practices/ui";

export function PracticeInfoSettingsV2Route() {
  const practice = useCurrentPractice();
  const practiceId = usePracticeId();
  const canEditPractice = useCanEditPractice();
  const queryClient = useQueryClient();
  const updatePractice = useUpdatePractice();

  const initialValues = useMemo(
    () => PracticeEntity.toInfoFormValues(practice),
    [practice],
  );

  const saveError =
    updatePractice.error instanceof Error
      ? updatePractice.error.message
      : updatePractice.isError
        ? "Unable to save practice info."
        : null;

  const submit = useCallback(
    async (values: ReturnType<typeof PracticeEntity.toInfoFormValues>) => {
      await updatePractice.mutateAsync({
        id: practiceId,
        data: PracticeInfoUpdatePayloadSchema.cast(values),
      });

      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast.success("Practice info updated.");
    },
    [practiceId, queryClient, updatePractice],
  );

  return (
    <PracticeInfoSettingsPageV2
      initialValues={initialValues}
      isReadOnly={!canEditPractice}
      isSaving={updatePractice.isPending}
      onSubmit={submit}
      saveError={saveError}
    />
  );
}
